import { useMutation } from "@tanstack/react-query";

import { queryClient } from "@/config/react-query";

import createId from "@/lib/create-id";
import { compressImage } from "@/lib/image-compression";
import { MedicationLabelData } from "@/lib/medication-label";
import { parseError } from "@/lib/parse-error";
import { toast } from "@/lib/use-toast";

import type {
  MeasurementCreate,
  MeasurementSummary,
} from "@/types/measurement";
import type {
  DoseManagementStatus,
  MedicationCreate,
  MedicationSummary,
  MedicationUpdate,
} from "@/types/medication";
import type {
  DynamicHealthFieldValue,
  MergeSummary,
  PatientChart,
  PatientCreate,
  PatientSummary,
  PatientUpdate,
} from "@/types/patient";
import type { PatientSidebarCounts } from "@/types/patient-sidebar";
import type { PatientTag } from "@/types/patient-tag";
import type { PaymentMethod } from "@/types/payment";

import { aiSummaryQueryKeys } from "./ai-summary-query";
import {
  type PatientMedicationState,
  addMeasurement,
  archivePatients,
  changeBlacklistStatus,
  createMedication,
  createPatient,
  createPaymentMethod,
  deleteMedication,
  deletePaymentMethod,
  mergeDuplicates,
  saveDoseLabel,
  signOffMedication,
  updateDoseManagementStatus,
  updateMedication,
  updatePatient,
  updatePatientDynamicFields,
  updatePatientPreferredProvider,
  updatePatientProfilePicture,
} from "./patient";
import {
  addPatientToTables,
  addTagsToPatientsInCache,
  buildOptimisticSummaryFromCreate,
  removePatientFromMismatchedFilteredCaches,
  removePatientsFromTables,
  setPatientTagsInCache,
  updatePatientInCache,
} from "./patient-cache-utils";
import { patientQueryKeys } from "./patient-query";
import {
  type BulkPatientTagAssignmentResponse,
  assignPatientTags,
  bulkAssignPatientTags,
} from "./patient-tag";
import {
  optimisticAdjustSidebarCounts,
  revertSidebarCounts,
} from "./sidebar-cache-utils";
import { getSignedUrl } from "@/api/auth/gcs";
import { paymentQueryKeys } from "@/api/provider/billing/payment-query";
import { todaysItemsQueryKeys } from "@/api/provider/billing/todays-items-query";
import { invalidatePrefilledAnswers } from "@/api/provider/scribe/note-cache-utils";
import { cohortQueryKeys } from "@/api/query/provider/comms/blast";
import {
  invalidateByKey,
  revertOnError,
  startOptimisticChange,
} from "@/api/utils/query-helpers";
import { stockQueryKeys } from "@/query/inventory/stock";
import { medicationsQueryKeys } from "@/query/medications/medications";
import { measurementGoalQueryKeys } from "@/query/patient/measurement-goals";
import { measurementQueryKeys } from "@/query/patient/measurements";
import { authService } from "@/services/auth-service";

// Debounced measurement-query invalidation to prevent race conditions with rapid edits.
// Patient detail measurements are patched directly from the mutation response.
const invalidationTimeouts = new Map<string, NodeJS.Timeout>();

function debouncedInvalidateMeasurementQuery(
  patientId: string,
  delay: number = 500,
) {
  const existingTimeout = invalidationTimeouts.get(patientId);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
  }

  const timeout = setTimeout(() => {
    invalidateByKey(measurementQueryKeys.patient(patientId));
    invalidationTimeouts.delete(patientId);
  }, delay);

  invalidationTimeouts.set(patientId, timeout);
}

export const useUpdatePatientMutation = (patientId: string) =>
  useMutation<
    PatientSummary | null,
    Error,
    PatientUpdate,
    { snapshots: Array<[readonly unknown[], unknown]> }
  >({
    mutationFn: (data: PatientUpdate) => updatePatient(patientId, data),
    onMutate: async (update) => {
      await queryClient.cancelQueries({ queryKey: patientQueryKeys.all });
      const snapshots = updatePatientInCache(patientId, update);
      return { snapshots };
    },
    onSuccess: (data, update) => {
      if (data) updatePatientInCache(patientId, data);
      const affectedFilters = [
        "city" in update ? "city" : null,
        "creditBalance" in update ? "creditBalance" : null,
        "dateOfBirth" in update ? "age" : null,
        "dateOfBirth" in update ? "birthday" : null,
        "dateOfBirth" in update ? "dateOfBirth" : null,
        "email" in update ? "email" : null,
        "email" in update ? "hasEmail" : null,
        "externalId" in update ? "externalId" : null,
        "firstName" in update || "lastName" in update ? "name" : null,
        "gender" in update ? "gender" : null,
        "goalWeight" in update ? "goalWeight" : null,
        "leadSource" in update ? "leadSource" : null,
        "optedInToEmailMarketing" in update ? "optedInToEmailMarketing" : null,
        "optedInToSmsMarketing" in update ? "optedInToSmsMarketing" : null,
        "optedInToSocialMedia" in update ? "optedInToSocialMedia" : null,
        "phoneNumber" in update ? "phoneNumber" : null,
        "phoneNumber" in update ? "hasPhoneNumber" : null,
        "preferredName" in update ? "preferredName" : null,
        "primaryLocationId" in update ? "locationIds" : null,
        "preferredProviderId" in update ? "preferredProviderId" : null,
        "pronouns" in update ? "pronouns" : null,
        "state" in update ? "state" : null,
        "surchargeDisabled" in update ? "surchargeDisabled" : null,
        "zipCode" in update ? "zipCode" : null,
      ].filter(
        (
          filter,
        ): filter is Parameters<
          typeof removePatientFromMismatchedFilteredCaches
        >[1][number] => Boolean(filter),
      );
      if (affectedFilters.length > 0) {
        removePatientFromMismatchedFilteredCaches(
          { id: patientId, ...update, ...(data ?? {}) },
          affectedFilters,
        );
      }
    },
    onError: (_err, _vars, ctx) => {
      ctx?.snapshots.forEach(([key, value]) => {
        queryClient.setQueryData(key, value);
      });
      // Error handling is now done at the component level for better UX
    },
    onSettled: () => {
      invalidateByKey(patientQueryKeys.editHistory(patientId));
      invalidateByKey(measurementQueryKeys.patient(patientId));
      invalidateByKey(measurementGoalQueryKeys.patient(patientId));
      queryClient.invalidateQueries({
        queryKey: [...patientQueryKeys.all, "duplicates"],
      });
      queryClient.invalidateQueries({
        queryKey: [...patientQueryKeys.all, "duplicates-of-patient"],
      });
    },
  });

export const useUpdatePatientDynamicFieldsMutation = (patientId: string) =>
  useMutation<
    { healthData: PatientChart["healthData"] } | null,
    Error,
    Record<string, (string | DynamicHealthFieldValue)[]>,
    { previous?: PatientChart; key: readonly unknown[] }
  >({
    mutationFn: (fieldsById) =>
      updatePatientDynamicFields(patientId, fieldsById),
    onMutate: async (fieldsById) => {
      const key = patientQueryKeys.detail(patientId);
      const { previous } = await startOptimisticChange<
        PatientChart | undefined
      >(key, () => {
        const prev = queryClient.getQueryData<PatientChart | undefined>(key);
        if (prev && prev.healthData) {
          const next: PatientChart = {
            ...prev,
            healthData: { ...prev.healthData },
          };
          const entries = Object.entries(next.healthData ?? {});
          entries.forEach(([fieldName, current]) => {
            const hasFieldId =
              current && typeof current === "object" && "fieldId" in current;
            const fid = hasFieldId ? current.fieldId : undefined;
            if (fid && fieldsById[fid] && next.healthData) {
              const values = fieldsById[fid];
              next.healthData[fieldName] = {
                ...current,
                values: values.map((value) =>
                  typeof value === "string" ? value : value.value,
                ),
                valueDetails: values.map((value) =>
                  typeof value === "string"
                    ? { value, conceptRxId: null, strengthFormRxId: null }
                    : {
                        value: value.value,
                        conceptRxId: value.conceptRxId ?? null,
                        strengthFormRxId: value.strengthFormRxId ?? null,
                      },
                ),
              };
            }
          });
          queryClient.setQueryData<PatientChart>(key, next);
        }
      });
      return { previous, key };
    },
    onSuccess: (data) => {
      if (data?.healthData) {
        updatePatientInCache(patientId, { healthData: data.healthData });
      }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.key) revertOnError(ctx.key, ctx.previous);
      toast({
        title: "Error",
        description: "Failed to update health info",
        variant: "error",
      });
    },
    onSettled: () => {
      // Invalidate prefilled answers since dynamic field values changed
      invalidatePrefilledAnswers();
    },
  });

export const useAddMeasurementMutation = () =>
  useMutation<
    MeasurementSummary | null,
    Error,
    MeasurementCreate,
    { previous?: PatientChart; key: readonly unknown[]; optimisticId?: string }
  >({
    mutationFn: (data) => addMeasurement(data),
    onMutate: async (payload) => {
      const key = patientQueryKeys.detail(payload.patientId);
      const optimisticId = createId("mes");
      const { previous } = await startOptimisticChange<
        PatientChart | undefined
      >(key, () => {
        const prev = queryClient.getQueryData<PatientChart | undefined>(key);
        if (prev) {
          const optimistic: MeasurementSummary = {
            id: optimisticId,
            patientId: payload.patientId,
            measurementDate:
              payload.measurementDate || new Date().toISOString(),
            weight: payload.weight,
            height: payload.height,
            systolicBp: payload.systolicBp,
            diastolicBp: payload.diastolicBp,
            pulse: payload.pulse,
            temperature: payload.temperature,
            waistCircumference: payload.waistCircumference,
            createdDate: new Date().toISOString(),
            noteId: payload.noteId,
            eventId: payload.eventId,
          };
          queryClient.setQueryData<PatientChart>(key, {
            ...prev,
            measurements: [optimistic, ...(prev.measurements || [])],
          });
        }
      });
      return { previous, key, optimisticId };
    },
    onSuccess: (data, variables, context) => {
      if (data && context?.key && context?.optimisticId) {
        // Update the cache with the actual server response
        // Read the latest cache state to handle concurrent mutations correctly
        queryClient.setQueryData<PatientChart>(context.key, (prev) => {
          if (!prev) return prev;

          // Get the current measurements (which may have been updated by other concurrent mutations)
          const measurements = prev.measurements || [];

          // Remove the optimistic measurement and any other measurements with the same optimistic ID
          // Also ensure we don't duplicate the server response if it's already there
          const filteredMeasurements = measurements.filter(
            (m) => m.id !== context.optimisticId && m.id !== data.id,
          );

          // Add the server response first so grouping logic processes it as the latest
          return {
            ...prev,
            measurements: [data, ...filteredMeasurements],
          };
        });
      }
      // Use debounced invalidation to prevent race conditions when editing multiple measurements quickly.
      // Patient detail is already updated above; only refresh the standalone measurements query.
      debouncedInvalidateMeasurementQuery(variables.patientId, 500);
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.key) revertOnError(ctx.key, ctx.previous);
      toast({
        title: "Error",
        description: "Failed to add measurement",
        variant: "error",
      });
    },
  });

function invalidateDoseInventoryCaches(patientId?: string) {
  invalidateByKey(medicationsQueryKeys.all);
  invalidateByKey(["lot-numbers"]);
  invalidateByKey(stockQueryKeys.all);
  invalidateByKey(todaysItemsQueryKeys.all);
  if (patientId) {
    invalidateByKey(patientQueryKeys.detail(patientId));
    invalidateByKey(patientQueryKeys.editHistory(patientId));
  }
}

// Shared logic for medication-creation-like mutations with optimistic update
export const useCreateMedicationMutation = () =>
  useMutation<
    MedicationSummary | null,
    Error,
    MedicationCreate,
    { previous?: PatientChart; key: readonly unknown[]; optimisticId: string }
  >({
    mutationFn: createMedication,
    onMutate: async (payload) => {
      await queryClient.cancelQueries({
        queryKey: patientQueryKeys.detail(payload.patientId),
      });
      const key = patientQueryKeys.detail(payload.patientId);
      const optimisticId = createId("med");
      const previous = queryClient.getQueryData<PatientChart | undefined>(key);
      if (previous) {
        const optimistic: MedicationSummary = {
          id: optimisticId,
          createdDate: new Date().toISOString(),
          ...payload,
        };
        queryClient.setQueryData<PatientChart>(key, {
          ...previous,
          medications: [optimistic, ...(previous.medications || [])],
        });
      }
      return { previous, key, optimisticId };
    },
    onSuccess: (data, _vars, ctx) => {
      if (!data) return;
      queryClient.setQueryData<PatientChart>(ctx.key, (prev) => {
        if (!prev) return prev;
        const medications = prev.medications || [];
        const filteredMedications = medications.filter(
          (m) => m.id !== ctx.optimisticId && m.id !== data.id,
        );
        return {
          ...prev,
          medications: [data, ...filteredMedications],
        };
      });
    },
    onError: async (err, _vars, ctx) => {
      if (ctx?.previous && ctx.key) {
        queryClient.setQueryData(ctx.key, ctx.previous);
      }
      toast({
        title: "Failed to create medication",
        description: await parseError(err, "Failed to create medication"),
        variant: "error",
      });
    },
    onSettled: (_res, _err, variables) => {
      invalidateDoseInventoryCaches(variables.patientId);
      // A new dose becomes a checkout "Today's Items" entry for this patient.
      queryClient.invalidateQueries({
        queryKey: todaysItemsQueryKeys.patient(variables.patientId),
      });
    },
  });

export const useUpdateMedicationMutation = () =>
  useMutation<
    PatientMedicationState | null,
    Error,
    { medicationId: string; update: MedicationUpdate },
    { snapshots: Array<{ key: readonly unknown[]; previous?: PatientChart }> }
  >({
    mutationFn: ({ medicationId, update }) =>
      updateMedication(medicationId, update),
    onMutate: async ({ medicationId, update }) => {
      await queryClient.cancelQueries({ queryKey: patientQueryKeys.all });
      const affectedKeys = queryClient
        .getQueriesData<PatientChart | undefined>({
          queryKey: patientQueryKeys.all,
        })
        .filter(([key]) =>
          Array.isArray(key) ? key.includes("detail") : false,
        );
      const snapshots: Array<{
        key: readonly unknown[];
        previous?: PatientChart;
      }> = [];
      for (const [key, data] of affectedKeys) {
        if (!data?.medications) continue;
        const idx = data.medications.findIndex((m) => m.id === medicationId);
        if (idx === -1) continue;
        snapshots.push({ key, previous: data });
        const updated = {
          ...data.medications[idx],
          ...update,
          // Requests intentionally contain no canonical lot/provenance fields. Keep the
          // saved snapshot visible until the server supplies the authoritative result.
          components: update.components.map((component) => ({
            ...data.medications![idx].components.find(
              (existing) => existing.id && existing.id === component.id,
            ),
            ...component,
          })),
        };
        const next: PatientChart = {
          ...data,
          medications: data.medications.map((m, i) =>
            i === idx ? updated : m,
          ),
        };
        queryClient.setQueryData(key, next);
      }
      return { snapshots };
    },
    onSettled: (data, _error, _variables, context) => {
      invalidateDoseInventoryCaches(
        data?.patientId ??
          context?.snapshots.find((snapshot) => snapshot.previous)?.previous
            ?.id,
      );
    },
    onSuccess: (data) => {
      if (!data) return;
      updatePatientInCache(data.patientId, { medications: data.medications });
      removePatientFromMismatchedFilteredCaches(
        { id: data.patientId, medications: data.medications },
        ["medicationCount"],
      );
      // The server retired the cached summary; drop ours so the chart renders anew.
      queryClient.setQueryData(aiSummaryQueryKeys.detail(data.patientId), null);
    },
    onError: async (err, _vars, ctx) => {
      if (ctx?.snapshots) {
        for (const s of ctx.snapshots) {
          if (s.previous) queryClient.setQueryData(s.key, s.previous);
        }
      }
      toast({
        title: "Failed to update medication",
        description: await parseError(err, "Failed to update medication"),
        variant: "error",
      });
    },
  });

export const useUpdateDoseManagementStatusMutation = () =>
  useMutation<
    PatientMedicationState | null,
    Error,
    { doseId: string; managementStatus: DoseManagementStatus | null },
    { snapshots: Array<{ key: readonly unknown[]; previous?: PatientChart }> }
  >({
    mutationFn: ({ doseId, managementStatus }) =>
      updateDoseManagementStatus(doseId, managementStatus),
    onMutate: async ({ doseId, managementStatus }) => {
      await queryClient.cancelQueries({ queryKey: patientQueryKeys.all });
      const affectedKeys = queryClient
        .getQueriesData<PatientChart | undefined>({
          queryKey: patientQueryKeys.all,
        })
        .filter(([key]) =>
          Array.isArray(key) ? key.includes("detail") : false,
        );
      const snapshots: Array<{
        key: readonly unknown[];
        previous?: PatientChart;
      }> = [];
      for (const [key, data] of affectedKeys) {
        if (!data?.medications) continue;
        const idx = data.medications.findIndex((m) => m.id === doseId);
        if (idx === -1) continue;
        snapshots.push({ key, previous: data });
        const updated = { ...data.medications[idx], managementStatus };
        const next: PatientChart = {
          ...data,
          medications: data.medications.map((m, i) =>
            i === idx ? updated : m,
          ),
        };
        queryClient.setQueryData(key, next);
      }
      return { snapshots };
    },
    onSuccess: (data) => {
      if (!data) return;
      updatePatientInCache(data.patientId, { medications: data.medications });
    },
    onError: async (err, _vars, ctx) => {
      if (ctx?.snapshots) {
        for (const s of ctx.snapshots) {
          if (s.previous) queryClient.setQueryData(s.key, s.previous);
        }
      }
      toast({
        title: "Failed to update dose status",
        description: await parseError(err, "Failed to update dose status"),
        variant: "error",
      });
    },
  });

export const useDeleteMedicationMutation = () =>
  useMutation<
    PatientMedicationState | null,
    Error,
    { medicationId: string },
    { snapshots: Array<{ key: readonly unknown[]; previous?: PatientChart }> }
  >({
    mutationFn: ({ medicationId }) => deleteMedication(medicationId),
    onMutate: async ({ medicationId }) => {
      await queryClient.cancelQueries({ queryKey: patientQueryKeys.all });
      const affectedKeys = queryClient
        .getQueriesData<PatientChart | undefined>({
          queryKey: patientQueryKeys.all,
        })
        .filter(([key]) =>
          Array.isArray(key) ? key.includes("detail") : false,
        );
      const snapshots: Array<{
        key: readonly unknown[];
        previous?: PatientChart;
      }> = [];
      for (const [key, data] of affectedKeys) {
        if (!data?.medications) continue;
        const exists = data.medications.some((m) => m.id === medicationId);
        if (!exists) continue;
        snapshots.push({ key, previous: data });
        const next: PatientChart = {
          ...data,
          medications: data.medications.filter((m) => m.id !== medicationId),
        };
        queryClient.setQueryData(key, next);
      }
      return { snapshots };
    },
    onError: async (err, _vars, ctx) => {
      if (ctx?.snapshots) {
        for (const s of ctx.snapshots) {
          if (s.previous) queryClient.setQueryData(s.key, s.previous);
        }
      }
      toast({
        title: "Failed to delete medication",
        description: await parseError(err, "Failed to delete medication"),
        variant: "error",
      });
    },
    onSettled: (data, _error, _variables, context) => {
      invalidateDoseInventoryCaches(
        data?.patientId ??
          context?.snapshots.find((snapshot) => snapshot.previous)?.previous
            ?.id,
      );
    },
    onSuccess: (data, _vars, ctx) => {
      if (data) {
        updatePatientInCache(data.patientId, { medications: data.medications });
        removePatientFromMismatchedFilteredCaches(
          { id: data.patientId, medications: data.medications },
          ["medicationCount"],
        );
        // The server retired the cached summary; drop ours so the chart renders anew.
        queryClient.setQueryData(
          aiSummaryQueryKeys.detail(data.patientId),
          null,
        );
        return;
      }

      const snapshot = ctx?.snapshots.find((item) => item.previous);
      const patient = snapshot
        ? queryClient.getQueryData<PatientChart>(snapshot.key)
        : undefined;
      if (patient) {
        updatePatientInCache(patient.id, { medications: patient.medications });
        removePatientFromMismatchedFilteredCaches(patient, ["medicationCount"]);
      }
    },
  });

export const useSignOffMedicationMutation = () =>
  useMutation<
    MedicationSummary | null,
    Error,
    { medicationId: string; patientId: string; currentProviderId?: string },
    { snapshots: Array<{ key: readonly unknown[]; previous?: PatientChart }> }
  >({
    mutationFn: ({ medicationId }) => signOffMedication(medicationId),
    onMutate: async ({ medicationId, currentProviderId }) => {
      await queryClient.cancelQueries({ queryKey: patientQueryKeys.all });
      const affectedKeys = queryClient
        .getQueriesData<PatientChart | undefined>({
          queryKey: patientQueryKeys.all,
        })
        .filter(([key]) =>
          Array.isArray(key) ? key.includes("detail") : false,
        );
      const snapshots: Array<{
        key: readonly unknown[];
        previous?: PatientChart;
      }> = [];
      for (const [key, data] of affectedKeys) {
        if (!data?.medications) continue;
        const idx = data.medications.findIndex((m) => m.id === medicationId);
        if (idx === -1) continue;
        snapshots.push({ key, previous: data });
        const updated = {
          ...data.medications[idx],
          signedOffProviderId: currentProviderId,
          signedOffDate: new Date().toISOString(),
        } as MedicationSummary;
        const next: PatientChart = {
          ...data,
          medications: data.medications.map((m, i) =>
            i === idx ? updated : m,
          ),
        };
        queryClient.setQueryData(key, next);
      }
      return { snapshots };
    },
    onSuccess: (data, { medicationId }) => {
      if (!data) return;
      const affectedKeys = queryClient
        .getQueriesData<PatientChart | undefined>({
          queryKey: patientQueryKeys.all,
        })
        .filter(([key]) =>
          Array.isArray(key) ? key.includes("detail") : false,
        );
      for (const [key, patient] of affectedKeys) {
        if (!patient?.medications?.some((m) => m.id === medicationId)) continue;
        queryClient.setQueryData<PatientChart>(key, {
          ...patient,
          medications: patient.medications.map((m) =>
            m.id === medicationId ? data : m,
          ),
        });
      }
    },
    onError: async (err, _vars, ctx) => {
      if (ctx?.snapshots) {
        for (const s of ctx.snapshots) {
          if (s.previous) queryClient.setQueryData(s.key, s.previous);
        }
      }
      toast({
        title: "Failed to sign off medication",
        description: await parseError(err, "Failed to sign off medication"),
        variant: "error",
      });
    },
  });

export const useSaveDoseLabelMutation = () =>
  useMutation<
    MedicationSummary | null,
    Error,
    {
      doseId: string;
      labelData: MedicationLabelData;
      action: "save" | "print";
    },
    { snapshots: Array<{ key: readonly unknown[]; previous?: PatientChart }> }
  >({
    mutationFn: ({ doseId, labelData, action }) =>
      saveDoseLabel(doseId, labelData, action),
    onMutate: async ({ doseId, labelData, action }) => {
      await queryClient.cancelQueries({ queryKey: patientQueryKeys.all });
      const affectedKeys = queryClient
        .getQueriesData<PatientChart | undefined>({
          queryKey: patientQueryKeys.all,
        })
        .filter(([key]) =>
          Array.isArray(key) ? key.includes("detail") : false,
        );
      const snapshots: Array<{
        key: readonly unknown[];
        previous?: PatientChart;
      }> = [];
      for (const [key, data] of affectedKeys) {
        if (!data?.medications) continue;
        const idx = data.medications.findIndex((m) => m.id === doseId);
        if (idx === -1) continue;
        snapshots.push({ key, previous: data });
        const updated = {
          ...data.medications[idx],
          labelData,
          ...(action === "print"
            ? { labelPrintedDate: new Date().toISOString() }
            : {}),
        } as MedicationSummary;
        const next: PatientChart = {
          ...data,
          medications: data.medications.map((m, i) =>
            i === idx ? updated : m,
          ),
        };
        queryClient.setQueryData(key, next);
      }
      return { snapshots };
    },
    onSuccess: (data, { doseId, action }) => {
      if (!data) return;
      const affectedKeys = queryClient
        .getQueriesData<PatientChart | undefined>({
          queryKey: patientQueryKeys.all,
        })
        .filter(([key]) =>
          Array.isArray(key) ? key.includes("detail") : false,
        );
      for (const [key, patient] of affectedKeys) {
        if (!patient?.medications?.some((m) => m.id === doseId)) continue;
        queryClient.setQueryData<PatientChart>(key, {
          ...patient,
          medications: patient.medications.map((m) =>
            m.id === doseId ? data : m,
          ),
        });
      }
      // Announce success only once the label has actually persisted.
      toast({ title: action === "print" ? "Label printed" : "Label saved" });
    },
    onError: async (err, { doseId, action }, ctx) => {
      // Roll back only the affected dose so concurrent edits elsewhere in the
      // chart survive a failed save.
      if (ctx?.snapshots) {
        for (const s of ctx.snapshots) {
          const prevMed = s.previous?.medications?.find((m) => m.id === doseId);
          const current = queryClient.getQueryData<PatientChart>(s.key);
          if (!prevMed || !current?.medications) continue;
          queryClient.setQueryData<PatientChart>(s.key, {
            ...current,
            medications: current.medications.map((m) =>
              m.id === doseId ? prevMed : m,
            ),
          });
        }
      }
      const fallback =
        action === "print"
          ? "Failed to save printed label"
          : "Failed to save label";
      toast({
        title: fallback,
        description: await parseError(err, fallback),
        variant: "error",
      });
    },
  });

export const useCreatePaymentMethodMutation = () =>
  useMutation<
    PaymentMethod | null,
    Error,
    {
      patientId: string;
      paymentMethodId: string;
      /**
       * Brand and last four for the row shown while the save is in flight.
       * Optional because only Rainforest hands them to the browser - Stripe
       * returns an id and nothing else - and the server's response replaces
       * this row either way.
       */
      preview?: { brand?: string; last4?: string };
    },
    {
      previous?: PaymentMethod[];
      key: readonly unknown[];
      previousSidebarCounts?: PatientSidebarCounts;
    }
  >({
    mutationFn: ({ patientId, paymentMethodId }) =>
      createPaymentMethod(patientId, paymentMethodId),
    onMutate: async ({ patientId, preview }) => {
      const key = paymentQueryKeys.methods(patientId);
      const { previous } = await startOptimisticChange<
        PaymentMethod[] | undefined
      >(key, () => {
        const prev = queryClient.getQueryData<PaymentMethod[] | undefined>(key);
        if (prev) {
          const optimistic = {
            id: createId("pm"),
            brand: preview?.brand,
            last4: preview?.last4,
          } as PaymentMethod;
          queryClient.setQueryData<PaymentMethod[]>(key, [optimistic, ...prev]);
        }
      });
      const previousSidebarCounts = optimisticAdjustSidebarCounts(patientId, {
        paymentMethodsCount: 1,
      });
      return { previous, key, previousSidebarCounts };
    },
    onError: (_err, vars, ctx) => {
      revertSidebarCounts(vars.patientId, ctx?.previousSidebarCounts);
      if (ctx?.key) revertOnError(ctx.key, ctx.previous);
      toast({
        title: "Error",
        description: "Failed to add payment method",
        variant: "error",
      });
    },
    onSettled: (_res, _err, variables) => {
      invalidateByKey(paymentQueryKeys.methods(variables.patientId));
    },
  });

export const useDeletePaymentMethodMutation = () =>
  useMutation<
    boolean | null,
    Error,
    { paymentMethodId: string; patientId: string },
    {
      previous?: PaymentMethod[];
      key: readonly unknown[];
      previousSidebarCounts?: PatientSidebarCounts;
    }
  >({
    mutationFn: ({ paymentMethodId, patientId }) =>
      deletePaymentMethod(paymentMethodId, patientId),
    onMutate: async ({ paymentMethodId, patientId }) => {
      const key = paymentQueryKeys.methods(patientId);
      const { previous } = await startOptimisticChange<
        PaymentMethod[] | undefined
      >(key, () => {
        const prev = queryClient.getQueryData<PaymentMethod[] | undefined>(key);
        if (prev) {
          queryClient.setQueryData<PaymentMethod[]>(
            key,
            prev.filter((m) => m.id !== paymentMethodId),
          );
        }
      });
      const previousSidebarCounts = optimisticAdjustSidebarCounts(patientId, {
        paymentMethodsCount: -1,
      });
      return { previous, key, previousSidebarCounts };
    },
    onError: (_err, vars, ctx) => {
      revertSidebarCounts(vars.patientId, ctx?.previousSidebarCounts);
      if (ctx?.key) revertOnError(ctx.key, ctx.previous);
      toast({
        title: "Error",
        description: "Failed to delete payment method",
        variant: "error",
      });
    },
    onSettled: (_res, _err, variables) => {
      invalidateByKey(paymentQueryKeys.methods(variables.patientId));
    },
  });

export const useUpdatePatientProfilePictureMutation = (patientId: string) =>
  useMutation<
    string | null,
    Error,
    { file?: File | null },
    { snapshots: Array<[readonly unknown[], unknown]> }
  >({
    mutationFn: ({ file }) => updatePatientProfilePicture(patientId, file),
    onMutate: async ({ file }) => {
      await queryClient.cancelQueries({ queryKey: patientQueryKeys.all });
      const optimisticUrl = file ? "updating" : "";
      const snapshots = updatePatientInCache(patientId, {
        profilePictureUrl: optimisticUrl,
        profileImageUrl: optimisticUrl,
      });
      return { snapshots };
    },
    onSuccess: (profilePictureUrl) => {
      const update = {
        id: patientId,
        profilePictureUrl,
        profileImageUrl: profilePictureUrl,
      } as const;
      updatePatientInCache(patientId, update);
      removePatientFromMismatchedFilteredCaches(update, ["hasProfilePicture"]);
    },
    onError: (_err, _vars, ctx) => {
      ctx?.snapshots.forEach(([key, value]) => {
        queryClient.setQueryData(key, value);
      });
      toast({
        title: "Error",
        description: "Failed to update profile picture",
        variant: "error",
      });
    },
    onSettled: () => {
      invalidateByKey(patientQueryKeys.editHistory(patientId));
    },
  });

const PROFILE_PICTURE_FROM_IMAGE_COMPRESSION = {
  // Backend rejects anything over 5MB — leave headroom so a re-encode that
  // lands slightly above target still passes.
  maxSizeMB: 4.5,
  maxWidthOrHeight: 2048,
  initialQuality: 0.9,
  fileType: "image/jpeg",
};

/** Normalize an image to a JPEG within the backend's 5MB limit and upload it. */
async function uploadProfilePictureImage(
  patientId: string,
  sourceFile: File,
): Promise<string | null> {
  // compressImage re-encodes to JPEG and rewrites the extension to match.
  const { file } = await compressImage(
    sourceFile,
    PROFILE_PICTURE_FROM_IMAGE_COMPRESSION,
  );

  // Compression is best-effort, not a hard cap — guard the backend's 5MB
  // limit here so the user gets a clear message instead of a generic 400.
  if (file.size > 5 * 1024 * 1024) {
    throw new Error("This image is too large to use as a profile photo.");
  }

  return updatePatientProfilePicture(patientId, file);
}

// No optimistic "updating" sentinel for the entry points below: unlike the
// avatar uploader (which masks it with a local loading flag), they leave other
// avatars on the page reading the cache — a placeholder URL would make them try
// to sign a GCS path named "updating" and 404. Just write the real URL once the
// upload resolves.
function writeProfilePictureToCache(
  patientId: string,
  profilePictureUrl: string | null,
) {
  const update = {
    id: patientId,
    profilePictureUrl,
    profileImageUrl: profilePictureUrl,
  } as const;
  updatePatientInCache(patientId, update);
  removePatientFromMismatchedFilteredCaches(update, ["hasProfilePicture"]);
}

/**
 * Set a patient's profile picture from a photo just taken with the camera.
 */
export const useCaptureProfilePictureMutation = () =>
  useMutation<string | null, Error, { patientId: string; image: Blob | File }>({
    mutationFn: ({ patientId, image }) =>
      uploadProfilePictureImage(
        patientId,
        image instanceof File
          ? image
          : new File([image], `profile-${Date.now()}.jpg`, {
              type: image.type || "image/jpeg",
            }),
      ),
    onSuccess: (profilePictureUrl, { patientId }) => {
      writeProfilePictureToCache(patientId, profilePictureUrl);
      toast({
        title: "Success",
        description: "Profile photo updated",
        variant: "success",
      });
    },
    onSettled: (_res, _err, { patientId }) => {
      invalidateByKey(patientQueryKeys.editHistory(patientId));
    },
  });

/**
 * Set a patient's profile picture from an image that already lives in GCS (a
 * gallery attachment or an uploaded document) rather than a freshly picked
 * file. Resolves a signed URL, fetches the bytes in the browser, normalizes
 * them to a JPEG within the backend's 5MB limit, then reuses the standard
 * profile-picture upload endpoint.
 */
export const useSetProfilePictureFromImageMutation = () =>
  useMutation<
    string | null,
    Error,
    { patientId: string; gcsPath?: string; signedUrl?: string; name?: string }
  >({
    mutationFn: async ({ gcsPath, signedUrl, name, patientId }) => {
      // Prefer re-signing from the GCS path: gallery/note attachment signed
      // URLs carry a short TTL, so a URL handed to us when the gallery opened
      // may already be stale. Fall back to the provided signed URL only when
      // there's no path to re-sign.
      let url: string | undefined;
      if (gcsPath) {
        const response = await getSignedUrl(gcsPath);
        url = response?.signedUrl ?? undefined;
      }
      if (!url) {
        url = signedUrl;
      }
      if (!url) {
        throw new Error("Could not resolve the image URL");
      }

      // Fetch through the server-side proxy: direct cross-origin fetches of
      // signed GCS URLs are blocked by the bucket's CORS policy.
      const token = await authService.getToken();
      const response = await fetch(
        `/api/proxy-image?url=${encodeURIComponent(url)}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!response.ok) {
        // The proxy caps fetches at 10MB (413) and only passes images (415).
        // Documents can be larger than that, so give a specific message.
        if (response.status === 413) {
          throw new Error("This image is too large to use as a profile photo.");
        }
        throw new Error("Failed to fetch the image");
      }
      const blob = await response.blob();
      const sourceFile = new File([blob], name || "profile.jpg", {
        type: blob.type || "image/jpeg",
      });
      return uploadProfilePictureImage(patientId, sourceFile);
    },
    onSuccess: (profilePictureUrl, { patientId }) => {
      writeProfilePictureToCache(patientId, profilePictureUrl);
      toast({
        title: "Success",
        description: "Profile photo updated",
        variant: "success",
      });
    },
    onError: (err) => {
      toast({
        title: "Error",
        description: err?.message || "Failed to set profile photo",
        variant: "error",
      });
    },
    onSettled: (_res, _err, { patientId }) => {
      invalidateByKey(patientQueryKeys.editHistory(patientId));
    },
  });

export const useMergePatientsMutation = () =>
  useMutation<
    PatientSummary | null,
    Error,
    { merges: MergeSummary[] },
    { snapshots: Array<[readonly unknown[], unknown]> }
  >({
    mutationFn: ({ merges }) => mergeDuplicates(merges),
    onMutate: async ({ merges }) => {
      await queryClient.cancelQueries({ queryKey: patientQueryKeys.all });
      const mergedIds = merges.flatMap((m) => m.merge?.map((p) => p.id) || []);
      const snapshots = removePatientsFromTables(mergedIds);
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.snapshots?.forEach(([key, value]) => {
        queryClient.setQueryData(key, value);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: patientQueryKeys.all });
    },
  });

export const useCreatePatientMutation = () =>
  useMutation<
    PatientSummary | null,
    Error,
    PatientCreate,
    { snapshots: Array<[readonly unknown[], unknown]> }
  >({
    mutationFn: (payload) => createPatient(payload),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: patientQueryKeys.all });
      const optimistic = buildOptimisticSummaryFromCreate(payload);
      const snapshots = addPatientToTables(optimistic);
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.snapshots?.forEach(([key, value]) => {
        queryClient.setQueryData(key, value);
      });
      toast({
        title: "Error",
        description: "Failed to create patient",
        variant: "error",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: patientQueryKeys.all });
    },
  });

export const useArchivePatientsMutation = () =>
  useMutation<
    boolean | null,
    Error,
    { patientIds: string[]; isArchived: boolean },
    { snapshots: Array<[readonly unknown[], unknown]> }
  >({
    mutationFn: ({ patientIds, isArchived }) =>
      archivePatients(patientIds, isArchived),
    onMutate: async ({ patientIds }) => {
      await queryClient.cancelQueries({ queryKey: patientQueryKeys.all });
      const snapshots = removePatientsFromTables(patientIds);
      return { snapshots };
    },
    onError: async (err, _vars, ctx) => {
      // restore previous pages
      ctx?.snapshots?.forEach(([key, value]) => {
        queryClient.setQueryData(key, value);
      });
      toast({
        title: "Error",
        // The archive refusal names the patient and says to cancel their
        // appointment first; a fixed string here would drop the only
        // instruction staff get.
        description: await parseError(err, "Failed to archive patient"),
        variant: "error",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: patientQueryKeys.all });
      // Archived patients leave every cohort, so their counts move too.
      queryClient.invalidateQueries({ queryKey: cohortQueryKeys.all });
    },
  });

export const useChangeBlacklistStatusMutation = () =>
  useMutation<
    boolean | null,
    Error,
    { patientId: string; status: boolean; patient?: PatientSummary },
    {
      previous?: PatientSummary[];
      previousDetail?: PatientChart;
    }
  >({
    mutationFn: ({ patientId, status }) =>
      changeBlacklistStatus(patientId, status),
    onMutate: async ({ status, patient, patientId }) => {
      await queryClient.cancelQueries({
        queryKey: patientQueryKeys.blacklist(),
      });
      await queryClient.cancelQueries({
        queryKey: patientQueryKeys.detail(patientId),
      });
      const key = patientQueryKeys.blacklist();
      const previous = queryClient.getQueryData<PatientSummary[] | undefined>(
        key,
      );
      if (previous) {
        if (status && patient) {
          queryClient.setQueryData<PatientSummary[]>(key, [
            patient,
            ...previous,
          ]);
        } else if (!status) {
          queryClient.setQueryData<PatientSummary[]>(
            key,
            previous.filter((p) => p.id.toString() !== patientId.toString()),
          );
        }
      }
      const detailKey = patientQueryKeys.detail(patientId);
      const previousDetail = queryClient.getQueryData<PatientChart | undefined>(
        detailKey,
      );
      if (previousDetail) {
        queryClient.setQueryData<PatientChart>(detailKey, {
          ...previousDetail,
          onSchedulingBlacklist: status,
        });
      }
      return { previous, previousDetail };
    },
    onError: (_err, { patientId }, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(patientQueryKeys.blacklist(), ctx.previous);
      }
      if (ctx?.previousDetail) {
        queryClient.setQueryData(
          patientQueryKeys.detail(patientId),
          ctx.previousDetail,
        );
      }
      toast({
        title: "Error",
        description: "Failed to update blacklist",
        variant: "error",
      });
    },
    onSettled: (_res, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: patientQueryKeys.blacklist() });
      invalidateByKey(patientQueryKeys.editHistory(variables.patientId));
    },
  });

export const useAssignPatientTagsMutation = () =>
  useMutation<
    PatientTag[] | null,
    Error,
    { patientId: string; tags: PatientTag[] },
    { snapshots: Array<[readonly unknown[], unknown]> }
  >({
    mutationFn: async ({ patientId, tags }) => {
      const res = await assignPatientTags(
        patientId,
        tags.map((t) => t.id),
      );
      // requestData returns null on failure; convert to an error so onError
      // runs and the optimistic cache update gets reverted.
      if (!res) throw new Error("Failed to update patient tags");
      return res;
    },
    onMutate: async ({ patientId, tags }) => {
      // Cancel in-flight detail reads so a stale GET (served by the read
      // replica, which can lag behind this write) can't clobber the update.
      await queryClient.cancelQueries({
        queryKey: [...patientQueryKeys.all, "detail"],
      });
      const snapshots = setPatientTagsInCache(patientId, tags);
      return { snapshots };
    },
    onSuccess: (serverTags, { patientId }) => {
      // Replace the optimistic tags with the authoritative set returned by the
      // POST (read back on the primary). We intentionally do NOT invalidate the
      // detail query here: that refetch hits the read replica, which can still
      // be behind this commit and would repaint the pre-save tags. The natural
      // staleTime refetch reconciles later, once the replica has caught up.
      if (serverTags) setPatientTagsInCache(patientId, serverTags);
    },
    onError: (_err, _vars, ctx) => {
      ctx?.snapshots?.forEach(([key, value]) => {
        queryClient.setQueryData(key, value);
      });
      toast({
        title: "Error",
        description: "Failed to update patient tags",
        variant: "error",
      });
    },
  });

export const useBulkAssignPatientTagsMutation = () =>
  useMutation<
    BulkPatientTagAssignmentResponse,
    Error,
    { patientIds: string[]; tags: PatientTag[] },
    { snapshots: Array<[readonly unknown[], unknown]> }
  >({
    mutationFn: async ({ patientIds, tags }) => {
      const res = await bulkAssignPatientTags(
        patientIds,
        tags.map((t) => t.id),
      );
      // requestData returns null on failure; convert to an error so onError
      // runs and the optimistic cache update gets reverted.
      if (!res) throw new Error("Failed to bulk-assign patient tags");
      return res;
    },
    onMutate: async ({ patientIds, tags }) => {
      // Cancel in-flight reads of the caches we're about to write so a stale
      // GET response can't clobber the optimistic update. Use partial keys so
      // a single cancelQueries call handles every detail/table query.
      await Promise.all([
        queryClient.cancelQueries({
          queryKey: [...patientQueryKeys.all, "table"],
        }),
        queryClient.cancelQueries({
          queryKey: [...patientQueryKeys.all, "detail"],
        }),
      ]);
      const snapshots = addTagsToPatientsInCache(patientIds, tags);
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.snapshots?.forEach(([key, value]) => {
        queryClient.setQueryData(key, value);
      });
      toast({
        title: "Error",
        description: "Failed to tag patients",
        variant: "error",
      });
    },
    onSettled: (_res, _err, { patientIds }) => {
      // Patient table queries are filter-aware (e.g., tag-exclusion filters);
      // optimistic updates can't reproduce that filtering, so refetch the
      // tables to drop rows that no longer match. Detail queries also refetch
      // so server-side associations replace the optimistic placeholders.
      queryClient.invalidateQueries({
        queryKey: [...patientQueryKeys.all, "table"],
      });
      // Tag-based cohorts gain or lose these patients.
      queryClient.invalidateQueries({ queryKey: cohortQueryKeys.all });
    },
  });

export const useUpdatePatientPreferredProviderMutation = (patientId: string) =>
  useMutation<
    boolean | null,
    Error,
    { preferredProviderId: string | null },
    { snapshots: Array<[readonly unknown[], unknown]> }
  >({
    mutationFn: ({ preferredProviderId }) =>
      updatePatientPreferredProvider(patientId, preferredProviderId),
    onMutate: async ({ preferredProviderId }) => {
      await queryClient.cancelQueries({ queryKey: patientQueryKeys.all });
      const snapshots = updatePatientInCache(patientId, {
        preferredProviderId,
      });
      return { snapshots };
    },
    onSuccess: (_data, { preferredProviderId }) => {
      removePatientFromMismatchedFilteredCaches(
        { id: patientId, preferredProviderId },
        ["preferredProviderId"],
      );
    },
    onError: (_err, _vars, ctx) => {
      ctx?.snapshots.forEach(([key, value]) => {
        queryClient.setQueryData(key, value);
      });
      toast({
        title: "Error",
        description: "Failed to update preferred provider",
        variant: "error",
      });
    },
    onSettled: () => {
      invalidateByKey(patientQueryKeys.editHistory(patientId));
    },
  });
